import React from "react";
import styled, { css } from "styled-components";
import { colors } from "../theme";

const TabsBar = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
  height: 64px;
  padding: 8px 0;
  z-index: 1000;
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  
  /* Safe area for notches and home indicator */
  padding-bottom: calc(8px + env(safe-area-inset-bottom, 0));
  height: calc(64px + env(safe-area-inset-bottom, 0));
  
  /* This ensures the nav is always above other content */
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  
  /* Desktop styles */
  @media (min-width: 768px) {
    position: sticky;
    top: 0;
    bottom: auto;
    left: 0;
    right: auto;
    width: 100%;
    height: 70px;
    padding: 12px 0;
    background: #ffffff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    
    ${props => props.$isTop && css`
      position: sticky;
      top: 0;
      z-index: 100;
    `}
  }
`;

const Tab = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 60px;
  opacity: ${props => (props.$active ? 1 : 0.7)};
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  background: none;
  border: none;
  cursor: pointer;
  padding: 12px 4px;
  color: ${props => (props.$active ? '#E31B23' : '#555')};
  font-size: 0.7rem;
  border-radius: 12px;
  margin: 0 2px;
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  
  &:hover {
    opacity: 1;
  }
  
  svg {
    margin-bottom: 4px;
    font-size: 1.2rem;
  }
  
  @media (min-width: 768px) {
    padding: 8px 12px;
    margin: 0 4px;
    font-size: 0.75rem;
    
    svg {
      margin-bottom: 6px;
      font-size: 1.4rem;
    }
  }
  
  /* Mobile indicator (bottom) */
  &::after {
    content: '';
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%) scaleX(${props => (props.$active ? 1 : 0.5)});
    width: 40%;
    height: 3px;
    background: linear-gradient(90deg, #E31B23, #FFD700, #006E33);
    border-radius: 4px 4px 0 0;
    opacity: ${props => (props.$active ? 1 : 0)};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  /* Touch feedback */
  &:active {
    background: rgba(0, 0, 0, 0.05);
    transform: scale(0.95);
  }
  
  /* Desktop indicator (top) */
  @media (min-width: 768px) {
    &::after {
      top: 0;
      bottom: auto;
      border-radius: 0 0 4px 4px;
      transform: translateX(-50%) scaleX(${props => (props.$active ? 1 : 0)});
      width: 40px;
    }
    
    &:hover {
      background: rgba(0, 0, 0, 0.02);
      transform: translateY(-2px);
      &::after {
        opacity: 1;
        transform: translateX(-50%) scaleX(0.5);
      }
    }
  }
  
  svg {
    width: 22px;
    height: 22px;
    margin-bottom: 6px;
    fill: ${props => (props.$active ? '#E31B23' : '#666')};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    filter: drop-shadow(0 2px 2px rgba(0,0,0,0.1));
    
    @media (min-width: 768px) {
      width: 24px;
      height: 24px;
      margin-bottom: 6px;
    }
  }
  
  span {
    font-size: 11px;
    letter-spacing: 0.3px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
    
    @media (min-width: 768px) {
      font-size: 11px;
      font-weight: 600;
    }
  }
  
  &:active {
    transform: scale(0.96);
    opacity: 0.9;
  }
`;

export default function NavTabs({ tabs, activeTab, onTabChange, isTop = false }) {
  return (
    <TabsBar $isTop={isTop}>
      {tabs.map((tab) => (
        <Tab
          key={tab.key}
          onClick={() => onTabChange(tab.key)}
          aria-label={tab.label}
          $active={activeTab === tab.key}
        >
          {tab.icon}
          <span style={{ fontSize: '0.7rem', marginTop: '4px' }}>{tab.label}</span>
        </Tab>
      ))}
    </TabsBar>
  );
}
